import { useResponsive } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Separator } from '@chakra-ui/react/separator';
import { Text } from '@chakra-ui/react/text';
import { useState } from 'react';

import JiraAppBanner, { useJiraAppBanner } from '@/components/banners/JiraAppBanner/JiraAppBanner';
import ContentWrapper from '@/components/page/ContentWrapper';
import ProtectedPage from '@/components/page/ProtectedPage';
import PublicBuildPage from '@/components/page/PublicBuildPage/PublicBuildPage';
import BuildsSection from '@/pages/App/AppDetails/components/BuildsSection/BuildsSection';
import { ScheduledBuild } from '@/pages/App/AppDetails/components/ScheduledBuilds/scheduledBuilds.types';
import BuildListFilters from '@/pages/App/AppDetails/types/BuildListFilters';
import AppDetailsPageProps from '@/pages/App/types/AppDetailsPageProps';

import { AppPageLayout } from '../components/AppPageLayout';
import BuildFilters from './components/BuildFilters';
import MachineMigrationOpportunitySection from './components/MachineMigrationOpportunitySection/MachineMigrationOpportunitySection';

export const AppDetailsPageContent = (
  props: AppDetailsPageProps & { setScheduledBuild: (build: ScheduledBuild) => void },
) => {
  const { app, abilities, isViewedAsPublicPage, setScheduledBuild } = props;

  const { isMobile } = useResponsive();

  const jiraAppBanner = useJiraAppBanner();

  const [filters, setFilters] = useState<BuildListFilters>({
    term: '',
    status: null,
    triggerType: null,
    branch: null,
    pipelineable: null,
    beforeDate: null,
    afterDate: null,
    artifactType: null,
  });

  return (
    <Box display="flex" flexDir="column" minH="100%">
      {/* The machine migration section is an actionable decision that disappears once resolved, so it takes the slot
          and the promo yields — this page shows at most one banner above the build list. */}
      {!isViewedAsPublicPage && !props.showMachineMigrationOpportunity && jiraAppBanner.isVisible && (
        <>
          <JiraAppBanner
            onDismiss={jiraAppBanner.onDismiss}
            marginBlock={['16', '32']}
            marginInline={['12', '32']}
            width="auto"
          />
          <Separator />
        </>
      )}
      {props.showMachineMigrationOpportunity && (
        <>
          <Box paddingBlock={[16, 32]} paddingInline={[12, 32]}>
            <MachineMigrationOpportunitySection appSlug={app.slug} />
          </Box>
          <Separator />
        </>
      )}
      <Box paddingTop={[16, 32]} paddingBottom={app.hasAnyBuild ? [0, 12] : [16, 32]} paddingInline={[0, 32]}>
        <Text as="h2" paddingLeft={[12, 0]} textStyle="heading/h2">
          Builds
        </Text>
        {app.hasAnyBuild && <BuildFilters appSlug={app.slug} setFilters={setFilters} />}
      </Box>
      {!isMobile && <Separator />}
      <Box backgroundColor="background/secondary" flex="1">
        <ContentWrapper display="flex" flexDirection="row" paddingY="24" gap="16" alignItems="start">
          <Box display="flex" flexDirection="column" flexGrow="1" maxWidth="100%" gap="16">
            <BuildsSection
              app={app}
              abilities={abilities}
              filters={filters}
              isViewedAsPublicPage={isViewedAsPublicPage}
              onUpdateScheduledBuild={setScheduledBuild}
            />
          </Box>
        </ContentWrapper>
      </Box>
    </Box>
  );
};

const AppDetailsPage = (props: AppDetailsPageProps) => {
  const {
    app,
    abilities,
    isViewedAsPublicPage,
    workflowsInfoShown,
    shouldShowFailedPaymentAlert,
    ai,
    isWatchingApp,
    isOwnerUser,
  } = props;

  const [scheduledBuild, setScheduledBuild] = useState<ScheduledBuild>();

  const layout = (
    <AppPageLayout
      abilities={abilities}
      app={app}
      ai={ai}
      currentPage="Builds"
      isOwnerUser={isOwnerUser}
      isViewedAsPublicPage={isViewedAsPublicPage}
      isWatchingApp={isWatchingApp}
      onStartOrScheduleBuildDialogClose={() => setScheduledBuild(undefined)}
      scheduledBuild={scheduledBuild}
      shouldShowFailedPaymentAlert={shouldShowFailedPaymentAlert}
      workflowsInfoShown={workflowsInfoShown}
    >
      <AppDetailsPageContent {...props} setScheduledBuild={setScheduledBuild} />
    </AppPageLayout>
  );

  if (isViewedAsPublicPage) {
    return (
      <PublicBuildPage withoutFooter minH="100vh">
        {layout}
      </PublicBuildPage>
    );
  }

  return (
    <ProtectedPage footerBackground="background/primary" withoutFooter minH="100vh" showLimitedTrialBanner>
      {layout}
    </ProtectedPage>
  );
};

export default AppDetailsPage;
